import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Loader2, Trash2, Copy, ImagePlus, Upload, X } from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_imagelink,
  delete_website_master_data,
  get_all_imagelink,
  APL_LINK,
} from "../ServiceConnection/serviceconnection.js";

// ─── Delete Modal ─────────────────────────────────────────────────────────────
const DeleteModal = ({ image, onConfirm, onClose, deleteLoader }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card shadow-2xl animate-fade-in">
      <div className="p-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <Trash2 className="h-6 w-6 text-destructive" />
        </div>
        <h3 className="mb-2 text-base font-bold text-foreground">
          Delete Image?
        </h3>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete this image? This cannot be undone.
        </p>
      </div>
      <div className="flex gap-3 border-t border-border p-4">
        <Button
          variant="outline"
          className="flex-1 rounded-xl"
          onClick={onClose}
          disabled={deleteLoader}
        >
          No
        </Button>
        <Button
          className="flex-1 gap-2 rounded-xl bg-destructive text-white hover:bg-destructive/90"
          onClick={onConfirm}
          disabled={deleteLoader}
        >
          {deleteLoader ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Yes, Delete
        </Button>
      </div>
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const AddImage = () => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [images, setImages] = useState([]);
  const [imageLinkBase, setImageLinkBase] = useState("");
  const [fetchLoader, setFetchLoader] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // ── GET ───────────────────────────────────────────────────────────────────
  const master_data_get = useCallback(async () => {
    try {
      setFetchLoader(true);
      const fd = new FormData();
      fd.append("start_date", "");
      fd.append("end_date", "");
      fd.append("flag", "1");
      fd.append("call_id", "0");
      const Response = await server_post_data(get_all_imagelink, fd);
      if (!Response.data.error) {
        setImages(Response.data.message.data_imagelink || []);
        setImageLinkBase(
          APL_LINK + Response.data.message.data_link_image,
        );
        console.log(APL_LINK + Response.data.message.data_link_image)
      } else {
        toast({
          title: "Error",
          description: Response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setFetchLoader(false);
    }
  }, []);

  useEffect(() => {
    master_data_get();
  }, [master_data_get]);

  // ── DELETE ────────────────────────────────────────────────────────────────
  const master_data_action_update = async (call_id, for_status_final) => {
    try {
      setDeleteLoader(true);
      const fd = new FormData();
      fd.append("id_for_delete", call_id);
      fd.append("flag_for", "11");
      fd.append("for_status_final", for_status_final);
      const Response = await server_post_data(delete_website_master_data, fd);
      if (!Response.data.error) {
        toast({ title: "Deleted", description: Response.data.message });
        setDeleteTarget(null);
        master_data_get();
      } else {
        toast({
          title: "Error",
          description: Response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoader(false);
    }
  };

  // ── SAVE ──────────────────────────────────────────────────────────────────
  const handleSaveChangesdynamic = async () => {
    if (!selectedFile) {
      toast({
        title: "No image selected",
        description: "Please choose an image before uploading.",
        variant: "destructive",
      });
      return;
    }
    try {
      setSaveLoader(true);
      const fd = new FormData();
      fd.append("doctor_image_new", selectedFile); // same field name as old code
      const Response = await server_post_data(save_update_imagelink, fd);
      if (!Response.data.error) {
        toast({ title: "Success", description: Response.data.message });
        master_data_get();
        resetUpload();
      } else {
        toast({
          title: "Error",
          description: Response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaveLoader(false);
    }
  };

  // ── File handling — same validation as old code ────────────────────────────
  const processFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please select a valid image file.",
        variant: "destructive",
      });
      return;
    }

    if (file.size < 10 * 1024) {
      toast({
        title: "File too small",
        description: "File size is below the minimum limit (10KB).",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 500 * 1024) {
      toast({
        title: "File too large",
        description: "File size exceeds the maximum limit (500KB).",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setPreviewSrc(reader.result);
    reader.readAsDataURL(file);
    setSelectedFile(file);
  };

  const handleFileChange = (e) => {
    processFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setPreviewSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Copy to clipboard — same as old copyToClipboard ──────────────────────
  const copyToClipboard = (link) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link);
    } else {
      // fallback — same as old code
      const input = document.createElement("input");
      input.value = link;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    toast({
      title: "Link Copied",
      description: "Image URL copied to clipboard.",
    });
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-lg font-bold text-foreground">Image Library</h2>
          <p className="text-sm text-muted-foreground">
            Upload and manage images. Copy links to use them in blog posts and
            pages.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          {/* ── Left: Image Grid ─────────────────────────────────────────── */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">
                Uploaded Images
                {!fetchLoader && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    ({images.length} total)
                  </span>
                )}
              </p>
            </div>

            {/* Loading */}
            {fetchLoader && (
              <Card>
                <CardContent className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
              </Card>
            )}

            {/* Empty */}
            {!fetchLoader && images.length === 0 && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                    <ImagePlus className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    No images uploaded yet
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Upload your first image using the form on the right.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Image grid */}
            {!fetchLoader && images.length > 0 && (
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
                {images.map((img, index) => (
                  <div
                    key={img.primary_id || index}
                    className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    {/* Image */}
                    <div className="aspect-square overflow-hidden bg-muted">
                      <img
                        src={imageLinkBase + img.images_name}
                        alt={img.images_name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          e.target.src = "";
                          e.target.parentElement.classList.add(
                            "flex",
                            "items-center",
                            "justify-center",
                          );
                        }}
                      />
                    </div>

                    {/* Overlay on hover */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        onClick={() =>
                          copyToClipboard(imageLinkBase + img.images_name)
                        }
                        className="flex items-center gap-1.5 rounded-xl bg-white/90 px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-white transition-colors"
                      >
                        <Copy className="h-3 w-3" /> Copy Link
                      </button>
                      <button
                        onClick={() => setDeleteTarget(img)}
                        className="flex items-center gap-1.5 rounded-xl bg-destructive/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-destructive transition-colors"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>

                    {/* Bottom bar */}
                    <div className="border-t border-border p-2">
                      <p className="truncate text-[10px] text-muted-foreground">
                        {img.images_name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Right: Upload Form ───────────────────────────────────────── */}
          <div className="space-y-4">
            <Card>
              <div className="border-b border-border px-6 py-4">
                <h3 className="text-sm font-bold text-foreground">
                  Upload New Image
                </h3>
                <p className="text-xs text-muted-foreground">
                  JPG, JPEG, PNG · 10KB – 500KB
                </p>
              </div>
              <CardContent className="p-6 space-y-4">
                {/* Drop zone */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center transition-all ${
                    dragOver
                      ? "border-primary bg-primary/5"
                      : "border-border bg-muted/30 hover:border-primary/40 hover:bg-primary/5"
                  }`}
                >
                  {previewSrc ? (
                    <div className="relative w-full">
                      <img
                        src={previewSrc}
                        alt="Preview"
                        className="mx-auto max-h-48 rounded-xl object-cover"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetUpload();
                        }}
                        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-white hover:bg-destructive/90 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                        <Upload className="h-5 w-5 text-primary" />
                      </div>
                      <p className="text-sm font-semibold text-foreground">
                        Drop image here or click to browse
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        JPG, JPEG, PNG supported
                      </p>
                    </>
                  )}

                  {/* Hidden file input — same name as old code: doctor_image */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="doctor_image"
                    id="imgInput"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>

                {/* File info */}
                {selectedFile && (
                  <div className="rounded-xl border border-border bg-muted/30 px-4 py-3">
                    <p className="truncate text-xs font-semibold text-foreground">
                      {selectedFile.name}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                )}

                {/* Constraints info */}
                <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 space-y-1">
                  <p className="text-[11px] font-semibold text-foreground">
                    Image requirements:
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    • Format: JPG, JPEG, PNG only
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    • Minimum size: 10 KB
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    • Maximum size: 500 KB
                  </p>
                </div>

                {/* Upload button */}
                <Button
                  className="w-full gap-2 rounded-xl"
                  disabled={saveLoader || !selectedFile}
                  onClick={handleSaveChangesdynamic}
                >
                  {saveLoader ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Uploading...
                    </span>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" /> Upload Image
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Usage tip */}
            <Card>
              <CardContent className="p-5">
                <p className="mb-1.5 text-xs font-semibold text-foreground">
                  How to use
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  After uploading, hover over any image and click{" "}
                  <span className="font-semibold text-foreground">
                    Copy Link
                  </span>{" "}
                  to get the direct URL. Paste it in your blog posts, SEO image
                  fields, or any page content.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          image={deleteTarget}
          onConfirm={() =>
            master_data_action_update(deleteTarget.primary_id, "d")
          }
          onClose={() => setDeleteTarget(null)}
          deleteLoader={deleteLoader}
        />
      )}
    </>
  );
};

export default AddImage;
